import React from 'react'
import { Building2, Mail, Hourglass } from 'lucide-react'
import { EnrichedActionCard } from './EnrichedActionCard'
import type {
  EnrichedAction,
  SignalType,
  ActionTypeEnum,
  SentimentType,
} from '../../services/recommendations_api'
import type {
  ActionClickStatus,
  ActionClickUpdateStatus,
} from '../../services/recommendations_contracts'
import { cn } from '../UI/cn'
import { SIGNAL_ICON_COLORS, formatSignalLabel } from './constants'
import {
  dispatchActionClick,
  dispatchSentimentClick,
} from './actionCardHandlers'

interface CompanySectionProps {
  /**
   * Company name
   */
  companyName: string

  /**
   * Company ID (for keying)
   */
  companyId: number

  /**
   * All actions for this company
   */
  actions: EnrichedAction[]

  /**
   * Whether section is expanded
   */
  isExpanded: boolean

  /**
   * Toggle expansion callback
   */
  onToggle: () => void

  /**
   * Callback when an action card is clicked
   */
  onActionClick?: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum
  ) => void

  /**
   * Callback when user provides sentiment feedback (thumbs up/down)
   */
  onSentimentClick?: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum,
    sentiment: SentimentType | null
  ) => void

  /**
   * Function to get existing sentiment for an action (for persistence)
   */
  getSentimentForAction?: (action: EnrichedAction) => SentimentType | null

  /**
   * Callback when user clicks Done or Dismiss on an action card
   */
  onStatusClick?: (
    action: EnrichedAction,
    status: ActionClickUpdateStatus
  ) => void

  /**
   * Function to get existing status for an action
   */
  getStatusForAction?: (action: EnrichedAction) => ActionClickStatus | null
}

/**
 * Signal type icons mapping.
 * Uses shared color constants for consistency with other components.
 */
const SIGNAL_ICONS: Record<
  SignalType,
  React.ComponentType<{ size: number; className: string }>
> = {
  unreplied_email: Mail,
  inactive_deal: Hourglass,
}

/**
 * Renders the appropriate icon for a signal type with consistent styling.
 *
 * @param signalType - The type of signal (unreplied_email, inactive_deal)
 * @param size - Icon size in pixels (default: 12)
 * @returns Icon component or null if signal type is unknown
 */
const SignalIcon: React.FC<{ signalType: SignalType; size?: number }> = ({
  signalType,
  size = 12,
}) => {
  const IconComponent = SIGNAL_ICONS[signalType]
  if (!IconComponent) return null
  return (
    <IconComponent size={size} className={SIGNAL_ICON_COLORS[signalType]} />
  )
}

/**
 * Displays all actions for a single company.
 *
 * Shows company name with count, and all related actions underneath.
 * Allows users to focus on one relationship at a time.
 */
export const CompanySection: React.FC<CompanySectionProps> = ({
  companyName,
  companyId,
  actions,
  isExpanded,
  onToggle,
  onActionClick,
  onSentimentClick,
  getSentimentForAction,
  onStatusClick,
  getStatusForAction,
}) => {
  // Get subcategory from first action (all actions for same company share subcategory)
  const companySubcategory = actions[0]?.company_subcategory

  // Count actions by signal type
  const signalCounts = actions.reduce(
    (acc, action) => {
      acc[action.signal_type] = (acc[action.signal_type] || 0) + 1
      return acc
    },
    { unreplied_email: 0, inactive_deal: 0 } as Record<SignalType, number>
  )

  const handleCardClick = (action: EnrichedAction) => {
    dispatchActionClick(onActionClick, action, action.signal_type)
  }

  const handleSentimentClick = (
    action: EnrichedAction,
    sentiment: SentimentType | null
  ) => {
    dispatchSentimentClick(
      onSentimentClick,
      action,
      action.signal_type,
      sentiment
    )
  }

  return (
    <div className="rounded-lg border border-line bg-surface overflow-hidden">
      {/* Company Header - Always visible, clickable to expand/collapse */}
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-sunken transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-fb-green-50 text-fb-dark-green">
            <Building2 size={18} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-fg text-sm">{companyName}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              {companySubcategory && (
                <>
                  <span className="text-xs text-fg-secondary">
                    {companySubcategory}
                  </span>
                  <span className="text-line">·</span>
                </>
              )}
              {/* Signal type badges */}
              {(Object.entries(signalCounts) as [SignalType, number][])
                .filter(([, count]) => count > 0)
                .map(([signalType, count]) => (
                  <span
                    key={signalType}
                    className="inline-flex items-center gap-1 text-xs text-fg-secondary"
                  >
                    <SignalIcon signalType={signalType} />
                    <span>{count}</span>
                  </span>
                ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-fg-secondary bg-surface-alt px-2 py-0.5 rounded-full">
            {actions.length} {actions.length === 1 ? 'action' : 'actions'}
          </span>
          <svg
            className={cn(
              'w-5 h-5 text-fg-muted transition-transform',
              isExpanded && 'rotate-180'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Expanded content - Action cards */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-line-subtle pt-3">
          {actions.map((action, index) => (
            <div
              key={`${companyId}-${action.deal_id}-${index}`}
              className="relative"
            >
              {/* Signal type indicator on left */}
              <div className="absolute -left-2 top-3 flex items-center gap-1 text-xs text-fg-muted">
                <SignalIcon signalType={action.signal_type} />
              </div>
              <div className="ml-2">
                <EnrichedActionCard
                  action={action}
                  onCardClick={() => handleCardClick(action)}
                  onSentimentClick={(sentiment) =>
                    handleSentimentClick(action, sentiment)
                  }
                  initialSentiment={getSentimentForAction?.(action) ?? null}
                  onStatusClick={(status) => onStatusClick?.(action, status)}
                  actionStatus={getStatusForAction?.(action) ?? null}
                />
                {/* Quick context about why this action */}
                <div className="mt-1 ml-1 text-xs text-fg-muted">
                  {formatSignalLabel(
                    action.signal_type,
                    action.days_unreplied,
                    action.days_stale,
                    action.days_in_stage,
                    action.current_stage
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
